import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Building2, Stethoscope, Pill } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SearchBar from '../components/common/SearchBar';
import ListingCard from '../components/common/ListingCard';
import ListingMap from '../components/common/ListingMap';
import { citiesAPI } from '../lib/api';

const CityPage = () => {
  const { slug } = useParams();
  const [city, setCity] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCity();
  }, [slug]);

  const loadCity = async () => {
    setLoading(true);
    try {
      const res = await citiesAPI.getBySlug(slug);
      setCity(res.data);
      setListings(res.data.listings || []);
    } catch (error) {
      console.error('Error loading city:', error);
    } finally {
      setLoading(false);
    }
  };

  const facilities = listings.filter((l) => l.listing_type === 'facility');
  const specialists = listings.filter((l) => l.listing_type === 'specialist');
  const pharmacies = listings.filter((l) => l.listing_type === 'pharmacy');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-gray-200 rounded w-1/3" />
            <div className="h-6 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">City Not Found</h1>
          <Link to="/" className="text-teal-700 hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="city-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-50 to-white py-12 md:py-16 border-b">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center gap-2 text-gray-600 mb-4">
            <MapPin className="w-5 h-5" />
            <span>{city.state}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Healthcare Providers in {city.name}
          </h1>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl">
            {city.description || `Find top-rated hospitals, doctors, and pharmacies in ${city.name}, ${city.state}.`}
          </p>
          <SearchBar />
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
              <Building2 className="w-6 h-6 text-teal-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{facilities.length}</p>
              <p className="text-sm text-gray-600">Facilities</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
              <Stethoscope className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{specialists.length}</p>
              <p className="text-sm text-gray-600">Specialists</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-gray-200 text-center">
              <Pill className="w-6 h-6 text-rose-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{pharmacies.length}</p>
              <p className="text-sm text-gray-600">Pharmacies</p>
            </div>
          </div>

          {/* Map */}
          <div className="mb-8">
            <ListingMap listings={listings} className="h-[400px]" />
          </div>

          {/* Tabs */}
          <Tabs defaultValue="all" className="space-y-6">
            <TabsList>
              <TabsTrigger value="all">All ({listings.length})</TabsTrigger>
              <TabsTrigger value="facilities">Facilities ({facilities.length})</TabsTrigger>
              <TabsTrigger value="specialists">Specialists ({specialists.length})</TabsTrigger>
              <TabsTrigger value="pharmacies">Pharmacies ({pharmacies.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map((listing) => (
                  <ListingCard key={listing.listing_id} listing={listing} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="facilities">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {facilities.map((listing) => (
                  <ListingCard key={listing.listing_id} listing={listing} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="specialists">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {specialists.map((listing) => (
                  <ListingCard key={listing.listing_id} listing={listing} />
                ))}
              </div>
            </TabsContent>

            <TabsContent value="pharmacies">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pharmacies.map((listing) => (
                  <ListingCard key={listing.listing_id} listing={listing} />
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default CityPage;
